import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { MessageCircle, HelpCircle, Star, Mail } from "lucide-react";

type Tab = "help" | "feedback";

const helpFormSchema = z.object({
  issueType: z.string().optional(),
  description: z.string().min(10, "Please provide at least 10 characters describing the issue"),
  email: z.string().email("Please enter a valid email address"),
});

const feedbackFormSchema = z.object({
  feedback: z.string().min(10, "Please provide at least 10 characters of feedback"),
  whatTrying: z.string().optional(),
  email: z.string().email("Please enter a valid email").optional().or(z.literal("")),
});

type HelpFormData = z.infer<typeof helpFormSchema>;
type FeedbackFormData = z.infer<typeof feedbackFormSchema>;

const ISSUE_OPTIONS = [
  { value: "cant_login", label: "I can't find what I'm looking for" },
  { value: "not_working", label: "Something isn't working correctly" },
  { value: "bug", label: "I found a bug" },
  { value: "billing", label: "I have a billing question" },
  { value: "other", label: "Other" },
];

function issueLabel(value: string) {
  return ISSUE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

export default function Feedback() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("help");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  const returnUrl = new URLSearchParams(window.location.search).get("returnUrl") || "/dashboard";
  const userEmail = user?.email ?? "";

  const helpForm = useForm<HelpFormData>({
    resolver: zodResolver(helpFormSchema),
    defaultValues: { issueType: "", description: "", email: userEmail },
  });

  const feedbackForm = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackFormSchema),
    defaultValues: { feedback: "", whatTrying: "", email: userEmail },
  });

  useEffect(() => {
    if (userEmail) {
      helpForm.setValue("email", userEmail);
      feedbackForm.setValue("email", userEmail);
    }
  }, [userEmail]);

  const submitMutation = useMutation({
    mutationFn: async (payload: { feedback: string; whatTrying?: string; email?: string }) => {
      return apiRequest("POST", "/api/feedback", {
        ...payload,
        pageUrl: returnUrl,
      });
    },
    onSuccess: () => {
      toast({ title: "Submitted!", description: "We read every submission and respond within 24 hours." });
      setLocation(returnUrl);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to submit. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onHelpSubmit = (data: HelpFormData) => {
    const typeLabel = data.issueType ? issueLabel(data.issueType) : "General";
    const composed = `[Help Request - ${typeLabel}]\n\n${data.description}`;
    submitMutation.mutate({ feedback: composed, email: data.email });
  };

  const onFeedbackSubmit = (data: FeedbackFormData) => {
    const ratingPrefix = rating > 0 ? `[Rating: ${rating}/5]\n\n` : "";
    const composed = `${ratingPrefix}${data.feedback}`;
    submitMutation.mutate({ feedback: composed, whatTrying: data.whatTrying, email: data.email });
  };

  return (
    <div className="min-h-screen">
      <MarketingHeader />
      <div className="max-w-2xl mx-auto px-6 py-16">

        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-5">
            <MessageCircle className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold mb-2" data-testid="text-feedback-title">
            How can we help?
          </h1>
          <p className="text-muted-foreground">
            We read every submission and respond within 24 hours.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-8">
          {(["help", "feedback"] as Tab[]).map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                data-testid={`tab-${tab}`}
                className={[
                  "flex flex-col items-center gap-1 rounded-md border-2 px-4 py-4 text-sm transition-colors",
                  isActive
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background text-muted-foreground hover-elevate",
                ].join(" ")}
              >
                {tab === "help" ? (
                  <HelpCircle className="w-5 h-5" />
                ) : (
                  <Star className="w-5 h-5" />
                )}
                <span className="font-semibold">
                  {tab === "help" ? "I need help" : "Share feedback"}
                </span>
                <span className="text-xs opacity-80">
                  {tab === "help" ? "Report an issue or get support" : "Suggestions and general thoughts"}
                </span>
              </button>
            );
          })}
        </div>

        {activeTab === "help" && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 bg-primary/10 rounded-md px-4 py-3 mb-6">
                <Mail className="w-4 h-4 text-primary shrink-0" />
                <p className="text-sm text-primary">
                  You can also email us directly at{" "}
                  <a href="mailto:hello@fillthetrucks.com" className="font-semibold underline underline-offset-2">
                    hello@fillthetrucks.com
                  </a>
                </p>
              </div>

              <Form {...helpForm}>
                <form onSubmit={helpForm.handleSubmit(onHelpSubmit)} className="space-y-5">
                  <FormField
                    control={helpForm.control}
                    name="issueType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>What type of issue are you experiencing?</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-issue-type">
                              <SelectValue placeholder="Select an issue type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {ISSUE_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={helpForm.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Describe the issue *</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Please describe what happened and what you were trying to do..."
                            className="min-h-[120px]"
                            data-testid="input-help-description"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={helpForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Your email *</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="so we can follow up with you"
                            data-testid="input-help-email"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col gap-2">
                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-primary text-primary-foreground"
                      disabled={submitMutation.isPending}
                      data-testid="button-submit-help"
                    >
                      {submitMutation.isPending ? "Submitting..." : "Submit Help Request"}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                      We'll respond within 24 hours
                    </p>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        )}

        {activeTab === "feedback" && (
          <Card>
            <CardContent className="pt-6">
              <Form {...feedbackForm}>
                <form onSubmit={feedbackForm.handleSubmit(onFeedbackSubmit)} className="space-y-5">
                  <div>
                    <p className="text-sm font-medium mb-3">How is your experience so far?</p>
                    <div className="flex gap-1" data-testid="star-rating">
                      {[1, 2, 3, 4, 5].map((star) => {
                        const filled = star <= (hoverRating || rating);
                        return (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setRating(star)}
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(0)}
                            data-testid={`star-${star}`}
                            className="p-0.5 transition-colors"
                          >
                            <Star
                              className={[
                                "w-7 h-7 transition-colors",
                                filled ? "text-primary fill-primary" : "text-muted-foreground",
                              ].join(" ")}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <FormField
                    control={feedbackForm.control}
                    name="feedback"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Your feedback *</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="What do you like? What could be better? Any features you'd love to see?"
                            className="min-h-[120px]"
                            data-testid="input-feedback-text"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={feedbackForm.control}
                    name="whatTrying"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>What were you trying to do? <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., Post a load, find available trucks..."
                            data-testid="input-feedback-trying"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={feedbackForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Your email <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="your@email.com"
                            data-testid="input-feedback-email"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col gap-2">
                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-primary text-primary-foreground"
                      disabled={submitMutation.isPending}
                      data-testid="button-submit-feedback"
                    >
                      {submitMutation.isPending ? "Submitting..." : "Submit Feedback"}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                      You'll be returned to the page you were on after submitting
                    </p>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        )}

      </div>
      <MarketingFooter />
    </div>
  );
}
